<template>
    <div>
        <div class="sort-toggle has-text-right" v-if="blocks.length > 1 || hasCollapsible">
            <b-button v-if="hasCollapsible && !sorting" size="is-small" class="mr-2" @click="toggleAll">
                <i class="fas mr-2" :class="allExpanded ? 'fa-compress-alt' : 'fa-expand-alt'"></i>{{ allExpanded ? 'Collapse All' : 'Expand All' }}
            </b-button>
            <b-button v-if="blocks.length > 1" size="is-small" :type="sorting ? 'is-primary' : ''" @click="sorting = !sorting">
                <i class="fas mr-2" :class="sorting ? 'fa-check' : 'fa-sort'"></i>{{ sorting ? 'Done Arranging' : 'Arrange Blocks' }}
            </b-button>
        </div>

        <draggable v-if="sorting" :list="blocks" :animation="200" ghost-class="sort-ghost">
            <div v-for="block in blocks" :key="block.keyId" class="sort-card">
                <div class="sort-grip"><i class="fas fa-grip-vertical"></i></div>
                <div class="collapsed-icon"><i :class="blockIcon(block)"></i></div>
                <div class="collapsed-info">
                    <div class="collapsed-type">{{ blockLabel(block) }}</div>
                    <div class="collapsed-summary">{{ blockSummary(block) || '(no content yet)' }}</div>
                </div>
            </div>
        </draggable>

        <div v-show="!sorting">
        <div class="add-content">
            <add-content @add="comp => addBlock(comp, 0)"/>
        </div>
                <div v-for="(block, i) in blocks" :key="block.keyId">
                    <div class="content-block" :class="{'is-editing': isCollapsible(block) && !isCollapsed(block)}">
                        <div class="content-block-component">
                            <div v-show="isCollapsed(block)" class="collapsed-block" @click="toggleBlock(block)">
                                <div class="collapsed-icon"><i :class="blockIcon(block)"></i></div>
                                <div class="collapsed-info">
                                    <div class="collapsed-type">{{ blockLabel(block) }}</div>
                                    <div class="collapsed-summary">{{ blockSummary(block) || 'Click to edit…' }}</div>
                                </div>
                                <div class="collapsed-tags">
                                    <span v-if="optionCount(block) !== null" class="tag is-light">{{ optionCount(block) }} option{{ optionCount(block) === 1 ? '' : 's' }}</span>
                                    <span v-if="block.content && block.content.required" class="tag is-warning is-light">Required</span>
                                    <i class="fas fa-pen edit-hint ml-2"></i>
                                </div>
                            </div>
                            <component
                                v-show="!isCollapsed(block)"
                                :is="block.component"
                                :data="block.content"
                                @save="(d) => block.content = d"
                            ></component>
                        </div>
                        <div class="content-block-controls">
                            <b-button v-if="isCollapsible(block) && !isCollapsed(block)" size="is-small" class="mr-2" title="Collapse" @click="toggleBlock(block)"><i class="fal fa-compress-alt"></i></b-button>
                            <b-button size="is-small" class="mr-2" @click="deleteBlock(i)"><i class="fal fa-trash-alt"></i></b-button>
<!--                            <i class="fas fa-sort drag-handle"></i>-->
                        </div>
                    </div>

                    <div class="add-content">
                        <add-content @add="comp => addBlock(comp, i+1)"/>
                    </div>
                </div>
        </div>
    </div>
</template>

<script>
    import AddContent from '@/components/editor/AddContent';

    import OneColumnText from '@/components/editor/content_blocks/OneColumnText';
    import TwoColumnText from '@/components/editor/content_blocks/TwoColumnText';
    import ThreeColumnText from '@/components/editor/content_blocks/ThreeColumnText';
    import ExpanderText from '@/components/editor/content_blocks/ExpanderText';
    import Video from '@/components/editor/content_blocks/Video';
    import FlipCards from '@/components/editor/content_blocks/FlipCards';
    import PhotoAndText from '@/components/editor/content_blocks/PhotoAndText';
    import TextField from '@/components/editor/content_blocks/TextField';
    import TextArea from '@/components/editor/content_blocks/TextArea';
    import CheckBox from '@/components/editor/content_blocks/CheckBox';
    import DropdownField from '@/components/editor/content_blocks/DropdownField';
    import StudyRecordLog from '@/components/editor/content_blocks/StudyRecordLog';
    import DistrictSchoolField from '@/components/editor/content_blocks/DistrictSchoolField';
    import ImageHotspot from '@/components/editor/content_blocks/ImageHotspot';
    import Message from '@/components/editor/content_blocks/Message';

    import draggable from "vuedraggable";

    const BLOCK_META = {
        // Input/special blocks collapse to summary cards in the editor
        TextField: {label: 'Text Field Question', icon: 'fad fa-keyboard', collapsible: true},
        TextArea: {label: 'Text Area Question', icon: 'fad fa-align-left', collapsible: true},
        CheckBox: {label: 'Checkbox/Switch', icon: 'fad fa-toggle-on', collapsible: true},
        DropdownField: {label: 'Dropdown Select Question', icon: 'fad fa-list-ul', collapsible: true},
        StudyRecordLog: {label: 'Study/Record Log', icon: 'fad fa-clipboard-list-check', collapsible: true, summary: "Displays the student's study/record log."},
        DistrictSchoolField: {label: 'District & School Selector', icon: 'fad fa-school', collapsible: true, summary: 'Applicant selects their district and school.'},
        // Display blocks only compact down in arrange mode
        OneColumnText: {label: 'Single Column Text', icon: 'fad fa-text'},
        TwoColumnText: {label: 'Two Column Text', icon: 'fad fa-columns'},
        ThreeColumnText: {label: 'Three Column Text', icon: 'fad fa-columns'},
        Message: {label: 'Text Message/Alert', icon: 'fad fa-comment-exclamation'},
        Video: {label: 'Library Video', icon: 'fad fa-video'},
        PhotoAndText: {label: 'Library Photo & Text', icon: 'fad fa-image'},
        ExpanderText: {label: 'Expand/Show More Text', icon: 'fad fa-hand-pointer'},
        FlipCards: {label: 'Flip Cards', icon: 'fad fa-clone'},
        ImageHotspot: {label: 'Image Hotspots', icon: 'fad fa-bullseye-pointer'},
    };

    export default {
        name: "ContentBlocks",
        components: {draggable, AddContent, OneColumnText, TwoColumnText, ThreeColumnText, ExpanderText, Video, FlipCards, ImageHotspot, PhotoAndText, TextField, TextArea, CheckBox, DropdownField, StudyRecordLog, Message, DistrictSchoolField},
        props: ['blocks'],
        computed: {
            collapsibleKeys() {
                return this.blocks.filter(b => this.isCollapsible(b)).map(b => b.keyId);
            },
            hasCollapsible() {
                return this.collapsibleKeys.length > 0;
            },
            allExpanded() {
                return this.collapsibleKeys.every(k => this.expandedKeys.includes(k));
            }
        },
        data: function() {
            return {
                expandedKeys: [],
                sorting: false
            };
        },
        methods: {
            addBlock(component, index) {
                const keyId = Math.random().toString(36).slice(2, 10);
                this.blocks.splice(index, 0, {
                    component,
                    content: {},
                    keyId
                });
                this.expandedKeys.push(keyId); // new blocks open in edit mode
            },
            meta(block) {
                return BLOCK_META[block.component] || {label: block.component, icon: 'fad fa-cube'};
            },
            isCollapsible(block) {
                return !!this.meta(block).collapsible;
            },
            isCollapsed(block) {
                return this.isCollapsible(block) && !this.expandedKeys.includes(block.keyId);
            },
            toggleBlock(block) {
                const i = this.expandedKeys.indexOf(block.keyId);
                if (i === -1) this.expandedKeys.push(block.keyId);
                else this.expandedKeys.splice(i, 1);
            },
            toggleAll() {
                if (this.allExpanded) {
                    this.expandedKeys = this.expandedKeys.filter(k => !this.collapsibleKeys.includes(k));
                } else {
                    for (const k of this.collapsibleKeys) {
                        if (!this.expandedKeys.includes(k)) this.expandedKeys.push(k);
                    }
                }
            },
            blockLabel(block) {
                return this.meta(block).label;
            },
            blockIcon(block) {
                return this.meta(block).icon;
            },
            blockSummary(block) {
                const d = block.content || {};
                let text = '';
                const areas = d.areas || d.columns || [];
                for (const a of areas) {
                    text = this.extractText(a && a.content);
                    if (text) break;
                }
                if (!text && d.text) text = this.extractText(d.text.content);
                if (!text && d.content) text = this.extractText(d.content);
                if (!text && d.option) text = d.option;
                if (!text && typeof d.title === 'string') text = d.title;
                if (!text && d.cards) text = d.cards.length + ' card' + (d.cards.length === 1 ? '' : 's');
                if (!text && d.spots) text = d.spots.length + ' hotspot' + (d.spots.length === 1 ? '' : 's');
                if (!text) text = this.meta(block).summary || '';
                return text;
            },
            extractText(areaContent) {
                if (!areaContent || !areaContent.blocks) return '';
                for (const b of areaContent.blocks) {
                    const text = (b.data && b.data.text) ? String(b.data.text).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim() : '';
                    if (text) return text;
                }
                return '';
            },
            optionCount(block) {
                if (block.component !== 'DropdownField') return null;
                return (block.content && block.content.options) ? block.content.options.length : 0;
            },
            deleteBlock(index) {
                if (window.confirm('Are you sure you want to delete this content?')) {
                    this.blocks.splice(index, 1);
                }
            }
        },
        created() {
            for (let i = 0; i < this.blocks.length; i++) {
                const block = this.blocks[i];
                block.keyId = Math.random().toString(36).slice(2, 10);
            }

            console.log(this.blocks);
        }
    };
</script>

<style lang="scss" scoped>
    .content-block {
        padding: 2px 0 2px 0;
        transition: border 500ms;
        display: flex;

        .content-block-controls {
            visibility: hidden;
            padding-left: 20px;
        }

        .content-block-component {
            flex: 1 1 0;
            min-width: 0;
            border-radius: 6px;
            border: 1px solid transparent;
        }

        &:hover {
            .content-block-controls {
                visibility: visible;
            }

            .content-block-component {
                border: 1px solid #e7e7e7;
            }
        }

        &.is-editing {
            .content-block-controls {
                visibility: visible;
            }

            .content-block-component {
                border: 1px solid #e7e7e7;
            }
        }
    }

    .collapsed-block {
        display: flex;
        align-items: center;
        padding: 0.6rem 1rem;
        border: 1px solid #e7e7e7;
        border-radius: 6px;
        background-color: #fafafa;
        cursor: pointer;
        transition: background-color 300ms, border-color 300ms;

        .collapsed-icon {
            flex: 0 0 auto;
            width: 2.2rem;
            text-align: center;
            font-size: 1.1rem;
            color: #336699;
        }

        .collapsed-info {
            flex: 1 1 auto;
            min-width: 0;
            padding: 0 0.8rem;

            .collapsed-type {
                font-size: 0.7rem;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                color: #999;
            }

            .collapsed-summary {
                font-weight: bold;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .collapsed-tags {
            flex: 0 0 auto;
            display: flex;
            align-items: center;

            .tag {
                margin-left: 0.4rem;
            }

            .edit-hint {
                color: #cacaca;
                transition: color 300ms;
            }
        }

        &:hover {
            background-color: #f0f4f8;
            border-color: #336699;

            .edit-hint {
                color: #336699;
            }
        }
    }

    .sort-toggle {
        margin-bottom: 0.5rem;
    }

    .sort-card {
        display: flex;
        align-items: center;
        padding: 0.6rem 1rem;
        margin-bottom: 0.5rem;
        border: 1px solid #e7e7e7;
        border-radius: 6px;
        background-color: #fafafa;
        cursor: grab;

        &:active {
            cursor: grabbing;
        }

        .sort-grip {
            flex: 0 0 auto;
            margin-right: 0.8rem;
            color: #cacaca;
        }

        .collapsed-icon {
            flex: 0 0 auto;
            width: 2.2rem;
            text-align: center;
            font-size: 1.1rem;
            color: #336699;
        }

        .collapsed-info {
            flex: 1 1 auto;
            min-width: 0;
            padding: 0 0.8rem;

            .collapsed-type {
                font-size: 0.7rem;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                color: #999;
            }

            .collapsed-summary {
                font-weight: bold;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        &:hover {
            border-color: #336699;

            .sort-grip {
                color: #336699;
            }
        }
    }

    .sort-ghost {
        opacity: 0.4;
        border: 1px dashed #336699;
    }
</style>
